import React from "react";
import { motion } from "framer-motion";
import {
  FaCode,
  FaDatabase,
  FaServer,
  FaLaptopCode,
  FaPalette,
  FaCheckCircle,
  FaRocket,
  FaLayerGroup,
  FaJs,
  FaReact,
  FaNodeJs,
  FaCloud,
} from "react-icons/fa";
import "./FullStackDev.scss"; // New SCSS file
import ServicesCommon from "../ServicesCommon/ServicesCommon";
import { useNavigate } from "react-router-dom";
import FullStackHeroImg from "../../../assets/images/fullstackhero.png";
// --- Framer Motion Variants ---

const sectionFade = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.17, 0.55, 0.55, 1] },
  },
};

const listContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const listItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const FullStackDev = () => {
  const navigate = useNavigate();

  // Data for the 'Why Full Stack' section
  const advantages = [
    {
      icon: FaLayerGroup,
      title: "Holistic Development",
      detail: "Single team handles both frontend and backend, ensuring seamless integration.",
    },
    {
      icon: FaRocket,
      title: "Faster Deployment",
      detail: "Reduced dependencies and quick resolution of cross-stack issues speeds up launch.",
    },
    {
      icon: FaJs,
      title: "Cost Efficiency",
      detail: "Less complexity in team management and hiring specialized developers.",
    },
    {
      icon: FaCheckCircle,
      title: "Better Maintenance",
      detail: "Developers understand the full architecture, simplifying debugging and updates.",
    },
  ];

  // Data for the 'Technology Stack' section
  const techStack = [
    { icon: FaReact, name: "Frontend (React, Vue, Angular)" },
    { icon: FaNodeJs, name: "Backend (Node.js, Python, Java)" },
    { icon: FaDatabase, name: "Database (MongoDB, MySQL, PostgreSQL)" },
    { icon: FaCloud, name: "DevOps (AWS, Azure, Docker)" },
  ];

  return (
    <div className="fullstack-dev-page">
      {/* ------------------ 1. HERO SECTION (Using user's provided content) ------------------ */}
      <section className="hero-section-fullstack">
        <div className="hero-content-left">
          <motion.p
            className="hero-tag-fullstack"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            FULL STACK DEVELOPMENT
          </motion.p>

          <motion.h1
            className="hero-title-fullstack"
            variants={sectionFade}
            initial="hidden"
            animate="visible"
          >
            Full Stack Development that fulfills Your{" "}
            <motion.span
              initial={{ color: "#333" }}
              animate={{ color: "#BD2DC0" }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                repeatType: "mirror",
              }}
            >
              Vision
            </motion.span>
          </motion.h1>

          <motion.p
            className="hero-subtext-fullstack"
            variants={listItem}
            initial="hidden"
            animate="visible"
          >
            Econsign Soft is the leading full-stack web development company which can provide many advantages for any company.
          </motion.p>

          <motion.p
            className="hero-desc-fullstack"
            variants={listItem}
            initial="hidden"
            animate="visible"
          >
            Full stack development refers to the end-to-end application software development, including the front end and back end. The front end consists of the user interface, and the back end takes care of the business logic and application workflows. We help in choosing the right technologies for the project development and testing both on the front end and the back end. We write clean code across the stack by following the best practices of the tools used.
          </motion.p>

          <motion.button
            className="primary-btn-fullstack"
            variants={listItem}
            initial="hidden"
            animate="visible"
            whileHover={{
              scale: 1.05,
              boxShadow: "0 10px 30px rgba(189, 45, 192, 0.4)",
            }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/contact-us")}
          >
            Contact Us
          </motion.button>
        </div>

       <motion.div
          className="fullstack-illustration" 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, type: "spring", stiffness: 100 }}
        >
          <img 
            src={FullStackHeroImg} 
            alt="Full Stack Development Layers" 
            className="fullstack-hero-image" 
          />
        </motion.div>
      </section>
      
      {/* ------------------ 2. TECHNOLOGY STACK SECTION ------------------ */}
      <section className="tech-stack-section">
        <motion.h2
          variants={sectionFade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          Our <span className="highlight-tech">Comprehensive</span> Technology Stack
        </motion.h2>

        <motion.div
          className="tech-stack-grid"
          variants={listContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {techStack.map((tech, i) => (
            <motion.div className="tech-card" key={i} variants={listItem}>
              <tech.icon className="tech-icon" />
              <h4>{tech.name}</h4>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ------------------ 3. WHY FULL STACK ADVANTAGES ------------------ */}
      <section className="advantages-section">
        <motion.h2
          variants={sectionFade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          Why Choose <span className="highlight-advantages">Full Stack</span> For Your Business?
        </motion.h2>

        <motion.div
          className="advantages-grid"
          variants={listContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {advantages.map((adv, i) => (
            <motion.div className="advantage-card" key={i} variants={listItem}>
              <adv.icon className="advantage-icon" />
              <h3>{adv.title}</h3>
              <p>{adv.detail}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ------------------ 4. COMMON SECTION ------------------ */}
      <ServicesCommon />
    </div>
  );
};

export default FullStackDev;